import React, { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MuiButton from '@mui/material/Button';
import LandingLayout from './components/LandingLayout';
import { DISTRICT_BY_SLUG, CurriculumLesson } from './data/district-registry';
import PressoLessonPreview from './PressoLessonPreview';

const SHADOW = '4px 4px 0px #1e1e1e';
const SHADOW_SM = '2px 2px 0px #1e1e1e';
const PRESSTO_PURPLE = '#5C4FB8';
const CREAM = '#F2EEE5';

const gradeLabel = (g: string) => (g === 'K' ? 'Kindergarten' : `${g} Grade`);

// A sample lesson used only to demo the product on re-engagement pages
// ("here is what a Pressto lesson looks like"), not a curriculum claim.
const SAMPLE_LESSON: CurriculumLesson | undefined =
  DISTRICT_BY_SLUG.peninsula?.lessons.find((l) => l.title.includes('Life Cycle')) ||
  DISTRICT_BY_SLUG.peninsula?.lessons[0];

const WHATS_NEW = [
  { icon: '🎯', title: 'Aligned to your curriculum', body: 'Writing activities mapped to the exact units and standards your district already teaches.' },
  { icon: '✨', title: 'AI Writing Buddy', body: 'Helps students find their own words and keep going — it never writes it for them.' },
  { icon: '🧩', title: 'Scaffolds built in', body: 'Every lesson has a writing plan, word banks, and sentence starters.' },
  { icon: '📰', title: 'School newspapers', body: 'Students publish real journalism — class and school-wide editions.' },
];

function CTAButton({ children, onClick, dark = true }: { children: React.ReactNode; onClick: () => void; dark?: boolean }) {
  return (
    <MuiButton
      variant="contained"
      onClick={onClick}
      sx={{
        backgroundColor: dark ? '#2C2C2C' : '#FFE082',
        color: dark ? '#fff' : '#2C2C2C',
        fontFamily: 'Montserrat, Inter, sans-serif', fontWeight: 700, fontSize: '1rem',
        textTransform: 'none', borderRadius: '6px', border: '2px solid #1e1e1e',
        boxShadow: SHADOW, px: 4, py: 1.5,
        '&:hover': { boxShadow: SHADOW_SM, transform: 'translate(2px,2px)' },
        transition: 'all 100ms',
      }}
    >
      {children}
    </MuiButton>
  );
}

function LessonCard({ lesson, onClick }: { lesson: CurriculumLesson; onClick: () => void }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        position: 'relative', borderRadius: '8px', border: '2px solid #1e1e1e',
        boxShadow: SHADOW, backgroundColor: '#fff', overflow: 'hidden', cursor: 'pointer',
        display: 'flex', flexDirection: 'column',
        '&:hover': { boxShadow: SHADOW_SM, transform: 'translate(2px,2px)' },
        transition: 'all 100ms',
      }}
    >
      <Box sx={{ height: 8, backgroundColor: PRESSTO_PURPLE }} />
      <Box sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', gap: 0.75, mb: 1.25, flexWrap: 'wrap' }}>
          <Chip label={gradeLabel(lesson.grade)} bg="#2C2C2C" color="#FFE082" />
          <Chip label={lesson.program} bg={PRESSTO_PURPLE} color="#fff" />
          {PLAN_LABELS[lesson.plan] && <Chip label={PLAN_LABELS[lesson.plan]} bg="#00897B" color="#fff" />}
          <Chip label="BETA" bg="#FF9800" color="#1e1e1e" />
        </Box>
        <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: '1rem', color: '#1e1e1e', mb: 1, lineHeight: 1.2 }}>
          {lesson.title}
        </Typography>
        <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.85rem', color: '#555', lineHeight: 1.5, mb: 1.5, flex: 1, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {lesson.prompt}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
          {lesson.standards.slice(0, 3).map((c) => (
            <Chip key={c} label={c} bg="#F5F5F5" color="#666" small />
          ))}
        </Box>
      </Box>
    </Box>
  );
}

const PLAN_LABELS: Record<string, string> = {
  'op-ed': 'Opinion', 'sequence': 'Sequence', 'cause-effect': 'Cause and Effect',
  'compare-contrast': 'Compare and Contrast', 'retell-the-narrative': 'Retell the Narrative',
  'story': 'Story', 'problem-solution': 'Problem and Solution',
  'personal-narrative': 'Personal Narrative', 'description': 'Description', 'basic': 'Basic',
};

function Chip({ label, bg, color, small }: { label: string; bg: string; color: string; small?: boolean }) {
  return (
    <Box sx={{
      backgroundColor: bg, color, borderRadius: '4px', px: small ? 0.75 : 1, py: 0.3,
      fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: small ? '0.6rem' : '0.65rem',
      textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap',
    }}>{label}</Box>
  );
}

export default function CurriculumDistrictPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [preview, setPreview] = useState<CurriculumLesson | null>(null);
  const district = slug ? DISTRICT_BY_SLUG[slug] : undefined;

  const byProgram = useMemo(() => {
    const map: Record<string, CurriculumLesson[]> = {};
    (district?.lessons || []).forEach((l) => { (map[l.program] = map[l.program] || []).push(l); });
    Object.values(map).forEach((ls) => ls.sort((a, b) => a.grade.localeCompare(b.grade)));
    return map;
  }, [district]);

  if (!district) {
    return (
      <LandingLayout>
        <Box sx={{ textAlign: 'center', py: 16, px: 3 }}>
          <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: '1.5rem', mb: 2 }}>
            District not found
          </Typography>
          <CTAButton onClick={() => navigate('/curriculum/rgr')}>See aligned lessons</CTAButton>
        </Box>
      </LandingLayout>
    );
  }

  // Re-engagement variant: existing adoption, curriculum not yet confirmed → lead with the
  // proven user base instead of a (potentially false) curriculum-alignment claim.
  const reEngage = district.lessons.length === 0;

  const previewModal = preview && (
    <PressoLessonPreview
      lesson={preview}
      onClose={() => setPreview(null)}
      onRegister={() => navigate('/register')}
      onPricing={() => navigate('/pricing')}
    />
  );

  if (reEngage) {
    return (
      <LandingLayout>
        {/* Hero — social proof led */}
        <Box sx={{ backgroundColor: CREAM, borderBottom: '2px solid #1e1e1e', py: { xs: 8, md: 12 }, px: { xs: 3, md: 6 } }}>
          <Box sx={{ maxWidth: 920, mx: 'auto', textAlign: 'center' }}>
            <Box sx={{ display: 'inline-block', backgroundColor: '#fff', border: '2px solid #1e1e1e', borderRadius: '4px', px: 1.5, py: 0.5, mb: 3, boxShadow: SHADOW_SM }}>
              <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                {district.state} · K–5 Writing
              </Typography>
            </Box>
            <Typography variant="h1" sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: { xs: '2.1rem', md: '3rem' }, lineHeight: 1.1, color: '#2C2C2C', mb: 2 }}>
              Welcome back,{' '}
              <Box component="span" sx={{ color: PRESSTO_PURPLE }}>{district.name}</Box>
            </Typography>
            {district.socialProof && (
              <Box sx={{ display: 'inline-block', backgroundColor: '#EDE9F7', border: '2px solid #1e1e1e', borderRadius: '6px', px: 2.5, py: 1, mb: 3, boxShadow: SHADOW_SM }}>
                <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: { xs: '0.95rem', md: '1.1rem' }, color: PRESSTO_PURPLE }}>
                  ✨ {district.socialProof}
                </Typography>
              </Box>
            )}
            <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.95rem', color: '#555', fontWeight: 700, mb: 2 }}>
              📍 {district.location}
            </Typography>
            <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '1.1rem', color: '#444', lineHeight: 1.6, mb: 4, maxWidth: 680, mx: 'auto' }}>
              Pressto has grown up since you last used it — writing lessons that scaffold every student, aligned to your curriculum and the {district.framework}. Here is what is new.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <CTAButton onClick={() => navigate('/login')}>Log back in →</CTAButton>
              <CTAButton dark={false} onClick={() => navigate('/pricing')}>See pricing</CTAButton>
            </Box>
          </Box>
        </Box>

        {/* What is new */}
        <Box sx={{ backgroundColor: '#fff', py: { xs: 6, md: 9 }, px: { xs: 3, md: 6 } }}>
          <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
            <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: '1.6rem', color: '#1e1e1e', mb: 4, textAlign: 'center' }}>
              What is new since you last used Pressto
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
              {WHATS_NEW.map((f) => (
                <Box key={f.title} sx={{ border: '2px solid #1e1e1e', borderRadius: '8px', boxShadow: SHADOW_SM, p: 3, backgroundColor: '#fff', display: 'flex', gap: 2 }}>
                  <Box component="span" sx={{ fontSize: '1.6rem', lineHeight: 1 }}>{f.icon}</Box>
                  <Box>
                    <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: '1.05rem', color: '#1e1e1e', mb: 0.5 }}>{f.title}</Typography>
                    <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.9rem', color: '#555', lineHeight: 1.5 }}>{f.body}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Sample lesson preview */}
        {SAMPLE_LESSON && (
          <Box sx={{ backgroundColor: '#F7F8FA', borderTop: '2px solid #1e1e1e', borderBottom: '2px solid #1e1e1e', py: { xs: 6, md: 8 }, px: { xs: 3, md: 6 } }}>
            <Box sx={{ maxWidth: 520, mx: 'auto', textAlign: 'center' }}>
              <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: '1.4rem', color: '#1e1e1e', mb: 1 }}>
                See what a Pressto lesson looks like now
              </Typography>
              <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.9rem', color: '#777', mb: 3 }}>
                Click to open the student view. (Sample shown is aligned to Amplify CKLA — yours would align to whatever {district.name} teaches.)
              </Typography>
              <LessonCard lesson={SAMPLE_LESSON} onClick={() => setPreview(SAMPLE_LESSON)} />
            </Box>
          </Box>
        )}

        {/* Bottom CTA */}
        <Box sx={{ backgroundColor: '#2C2C2C', py: { xs: 8, md: 10 }, px: 3, textAlign: 'center' }}>
          <Box sx={{ maxWidth: 620, mx: 'auto' }}>
            <Typography variant="h2" sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: { xs: '1.6rem', md: '2.2rem' }, color: '#fff', mb: 2 }}>
              Bring Pressto back to {district.name}
            </Typography>
            <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '1.05rem', color: 'rgba(255,255,255,0.7)', mb: 4 }}>
              Your teachers already know it. Now it scaffolds every student and aligns to what you already teach.
            </Typography>
            <CTAButton dark={false} onClick={() => navigate('/login')}>Log back in</CTAButton>
          </Box>
        </Box>
        {previewModal}
      </LandingLayout>
    );
  }

  return (
    <LandingLayout>
      {/* Hero */}
      <Box sx={{ backgroundColor: CREAM, borderBottom: '2px solid #1e1e1e', py: { xs: 8, md: 12 }, px: { xs: 3, md: 6 } }}>
        <Box sx={{ maxWidth: 900, mx: 'auto', textAlign: 'center' }}>
          <Box sx={{ display: 'inline-block', backgroundColor: '#fff', border: '2px solid #1e1e1e', borderRadius: '4px', px: 1.5, py: 0.5, mb: 3, boxShadow: SHADOW_SM }}>
            <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              {district.gradeRange} Writing Curriculum
            </Typography>
          </Box>
          <Typography variant="h1" sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: { xs: '2.2rem', md: '3rem' }, lineHeight: 1.1, color: '#2C2C2C', mb: 2 }}>
            Writing lessons aligned to{' '}
            <Box component="span" sx={{ color: PRESSTO_PURPLE }}>{district.name}</Box>
          </Typography>
          <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.95rem', color: '#555', fontWeight: 700, mb: 2 }}>
            📍 {district.location}
          </Typography>
          {district.socialProof && (
            <Box sx={{ display: 'inline-block', backgroundColor: '#EDE9F7', border: '2px solid #1e1e1e', borderRadius: '6px', px: 2.5, py: 1, mb: 3, boxShadow: SHADOW_SM }}>
              <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: { xs: '0.9rem', md: '1.05rem' }, color: PRESSTO_PURPLE }}>
                ✨ {district.socialProof}
              </Typography>
            </Box>
          )}
          <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '1.1rem', color: '#444', lineHeight: 1.6, mb: 4, maxWidth: 680, mx: 'auto' }}>
            {district.lessonCount} ready-to-use writing activities for grades {district.gradeRange}, aligned to your
            programs ({district.programs.join(', ')}) and {district.framework}.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <CTAButton onClick={() => navigate('/register')}>Try it free →</CTAButton>
            <CTAButton dark={false} onClick={() => navigate('/pricing')}>See pricing</CTAButton>
          </Box>
        </Box>
      </Box>

      {/* Beta note */}
      <Box sx={{ backgroundColor: '#FFF8E1', borderBottom: '2px solid #1e1e1e', py: 1.5, px: 3, textAlign: 'center' }}>
        <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.8rem', color: '#8a6d00' }}>
          ⚡ <b>Beta preview</b> — these activities are newly generated and aligned to your curriculum; still being vetted.
        </Typography>
      </Box>

      {/* Lessons grouped by program */}
      <Box sx={{ backgroundColor: '#fff', py: { xs: 6, md: 8 }, px: { xs: 3, md: 6 } }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          {district.programs.map((program) => (
            <Box key={program} sx={{ mb: 7 }}>
              <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: '1.4rem', color: '#1e1e1e', mb: 0.5 }}>
                {program}
              </Typography>
              <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.9rem', color: '#888', mb: 3 }}>
                {byProgram[program].length} writing activities aligned to {program}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 3 }}>
                {byProgram[program].map((lesson, i) => (
                  <LessonCard key={`${program}-${i}`} lesson={lesson} onClick={() => setPreview(lesson)} />
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Bottom CTA */}
      <Box sx={{ backgroundColor: '#2C2C2C', borderTop: '2px solid #1e1e1e', py: { xs: 8, md: 10 }, px: 3, textAlign: 'center' }}>
        <Box sx={{ maxWidth: 600, mx: 'auto' }}>
          <Typography variant="h2" sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: { xs: '1.6rem', md: '2.2rem' }, color: '#fff', mb: 2 }}>
            Bring aligned writing to {district.name}
          </Typography>
          <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '1.05rem', color: 'rgba(255,255,255,0.7)', mb: 4 }}>
            Every activity scaffolds students with structure, prompts, and sentence starters — aligned to what you already teach.
          </Typography>
          <CTAButton dark={false} onClick={() => navigate('/register')}>Get started free</CTAButton>
        </Box>
      </Box>
      {previewModal}
    </LandingLayout>
  );
}
